import React, { useState, useEffect } from 'react';
import {
  Container,
  Header,
  Table,
  Box,
  Button,
  SpaceBetween,
  Modal,
  Grid,
  Alert,
} from '@cloudscape-design/components';
import { HistorySummary, DemoRun } from '../types';
import { apiService } from '../services/ApiService';
import { ResponseCard } from './ResponseCard';
import { TEST_SCENARIOS } from '../config';

interface HistoryViewProps {
  userId: string;
}

export const HistoryView: React.FC<HistoryViewProps> = ({ userId }) => {
  const [history, setHistory] = useState<HistorySummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedRun, setSelectedRun] = useState<DemoRun | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [lastKey, setLastKey] = useState<string | undefined>(undefined);
  const [hasMore, setHasMore] = useState(true);

  useEffect(() => {
    loadHistory();
  }, [userId]);

  const loadHistory = async (loadMore: boolean = false) => {
    try {
      setLoading(true);
      setError(null);
      const result = await apiService.getHistory(
        userId,
        20,
        loadMore ? lastKey : undefined
      );
      
      if (loadMore) {
        setHistory(prev => [...prev, ...result.runs]);
      } else {
        setHistory(result.runs);
      }
      
      setLastKey(result.lastKey);
      setHasMore(!!result.lastKey);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load history');
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = async (demoRunId: string) => {
    try {
      setLoading(true);
      const run = await apiService.getDemoRun(demoRunId);
      setSelectedRun(run);
      setShowDetailModal(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load demo run details');
    } finally {
      setLoading(false);
    }
  };

  const formatTimestamp = (timestamp: number) => {
    return new Date(timestamp).toLocaleString();
  };

  const getTestName = (testId: number) => {
    const scenario = TEST_SCENARIOS.find(s => s.id === testId);
    return scenario?.name || `Test ${testId}`;
  };

  return (
    <SpaceBetween size="l">
      <Container
        header={
          <Header variant="h2" description="View your past demo runs">
            Demo Run History
          </Header>
        }
      >
        {error && <Alert type="error">{error}</Alert>}

        <Table
          columnDefinitions={[
            {
              id: 'timestamp',
              header: 'Date & Time',
              cell: item => formatTimestamp(item.timestamp),
              sortingField: 'timestamp',
            },
            {
              id: 'testId',
              header: 'Test Scenario',
              cell: item => getTestName(item.testId),
            },
            {
              id: 'models',
              header: 'Models',
              cell: item => item.models.join(', '),
            },
            {
              id: 'actions',
              header: 'Actions',
              cell: item => (
                <Button
                  variant="inline-link"
                  onClick={() => handleViewDetails(item.demoRunId)}
                >
                  View Details
                </Button>
              ),
            },
          ]}
          items={history}
          loading={loading}
          loadingText="Loading history..."
          empty={
            <Box textAlign="center" color="inherit">
              <b>No demo runs yet</b>
              <Box padding={{ bottom: 's' }} variant="p" color="inherit">
                Run a test scenario to see results here.
              </Box>
            </Box>
          }
          sortingDisabled={false}
        />

        {hasMore && (
          <Box textAlign="center" padding={{ top: 's' }}>
            <Button onClick={() => loadHistory(true)} disabled={loading}>
              Load More
            </Button>
          </Box>
        )}
      </Container>

      <Modal
        visible={showDetailModal}
        onDismiss={() => setShowDetailModal(false)}
        size="max"
        header={
          selectedRun ? `${getTestName(selectedRun.testId)} - ${formatTimestamp(selectedRun.timestamp)}` : 'Demo Run Details'
        }
      >
        {selectedRun && (
          <SpaceBetween size="l">
            <Box>
              <Box variant="h3">Responses</Box>
              <Grid gridDefinition={[
                { colspan: { default: 12, xs: 6, s: 4 } },
                { colspan: { default: 12, xs: 6, s: 4 } },
                { colspan: { default: 12, xs: 6, s: 4 } },
              ]}>
                {Object.values(selectedRun.responses).map(response => (
                  <ResponseCard key={response.modelId} response={response} />
                ))}
              </Grid>
            </Box>

            {selectedRun.round2Ratings && (
              <Box>
                <Box variant="h3">Round 2 Ratings</Box>
                <Box variant="p">
                  {JSON.stringify(selectedRun.round2Ratings, null, 2)}
                </Box>
              </Box>
            )}
          </SpaceBetween>
        )}
      </Modal>
    </SpaceBetween>
  );
};
